import React, { useRef, useState } from 'react';
import Calendar from './Calendar';
import { Popover, StyledEngineProvider } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Button, Input, Chip } from '../index';
import { getFormattedDate, groupDates } from 'utilities/DateUtils';
import { useController } from 'react-hook-form';
import { CalendarIcon } from 'assets/common';
import { useCalendar } from 'components/latest-core/Calendar/Calendar';
import { isNullOrUndefined } from 'utilities/Utils';
type CalendarProps = Pick<
React.ComponentProps<typeof Calendar>,
'disabledBefore' | 'disabledAfter' | 'showTodayButton' | 'todayBtnText'
>;
type InputProps = Pick<
React.ComponentProps<typeof Input>,
'rightElement' | 'disabled' | 'error' | 'size' | 'placeholder'
>;
type BlackOutCalendarProps = CalendarProps &
InputProps & {
  calendarProps: {
    selectedDays?: Date | Date[];
    setSelectedDays?: (sd: Date | Date[]) => void;
    selectedDateMeta?: {
      month?: { [key: string]: number };
      year?: { [key: string]: number };
      count?: number;
    };
    setSelectedDateMeta?: Function;
    month: Date;
    handleYearMonthChange: (_month: Date) => void;
  };
  blackoutEntryFormat?: string;
  shouldGroupTwoValues?: boolean;
  maxBlackoutEntries?: number;
  dateLabel?: string;
  doneBtnLabel?: string;
  initialDateOpen?: Date;
  className?: string;
  showYearChangeIcon?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
};
const blackoutEntry = (
  day,
  onDelete,
  format,
  index,
  className,
  baseIndex = 0,
) => {
  const firstDay = getFormattedDate(day[0], format);
  const lastDay = getFormattedDate(day[day.length - 1], format);
  const formattedLabel =
    day.length === 1 ? firstDay : `${firstDay} - ${lastDay}`;
  return (
    //TODO: Convert it in to chip
    <StyledEngineProvider injectFirst>
      <Chip
        className={className}
        label={formattedLabel}
        onDelete={() => {
          onDelete(index + baseIndex);
        }}
      />
    </StyledEngineProvider>
  );
};
const useStyles = makeStyles({
  rangeWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  chip: {
    margin: '4px 8px 0px 0px',
  },
  showMore: {
    padding: '3px 8px',
  },
});
export default function BlackoutCalendar(props: BlackOutCalendarProps) {
  const {
    calendarProps,
    disabled = false,
    error = false,
    size = 'medium',
    placeholder = 'Add Blackout Dates',
    showTodayButton = false,
    todayBtnText = 'Today',
    shouldGroupTwoValues = false,
    disabledBefore,
    disabledAfter,
    maxBlackoutEntries = 5,
    blackoutEntryFormat = 'MMM DD YYYY, ddd',
    dateLabel = '',
    doneBtnLabel = 'Done',
    showYearChangeIcon = false,
    initialDateOpen,
    className,
    inputRef,
  } = props;
  const containerRef = useRef();
  const {
    selectedDays,
    setSelectedDays,
    selectedDateMeta = { month: {}, year: {}, count: 0 },
    setSelectedDateMeta,
  } = calendarProps;
  const [showPopover, setShowPopover] = useState(false);
  const [isShowMore, setIsShowMore] = useState(false);
  const selectedDaysLen = (selectedDays as Date[]).length;
  const allDates = groupDates(selectedDays as Date[], shouldGroupTwoValues);
  const classes = useStyles();

  const handleRemoveDates = index => {
    const tempArr = [...allDates];
    const { month, year, count = 0 } = selectedDateMeta;
    const newCount = count - allDates[index].length;
    let datesArr = [];
    allDates[index].forEach(date => {
      const removeDate = new Date(date);
      const selectedDayMonth = removeDate.getMonth();
      const selectedDayYear = removeDate.getFullYear();
      const yearDateKey = `${selectedDayYear}-${selectedDayMonth}`;
      month[yearDateKey] = month[yearDateKey] - 1;
      year[selectedDayYear] = year[selectedDayYear] - 1;
    });
    setSelectedDateMeta({ month, year, count: newCount });
    tempArr.splice(index, 1);
    tempArr.forEach(dateArr => {
      const dateObjectArr = dateArr.map(date => new Date(date));
      datesArr = [...datesArr, ...dateObjectArr];
    });
    setSelectedDays([...datesArr]);
    calendarProps.handleYearMonthChange(datesArr[0] || new Date());
  };

  const showSelectedDays = () => {
    const first5Dates = allDates.slice(0, maxBlackoutEntries);
    const moreDates = allDates.slice(maxBlackoutEntries);
    const moreDatesLen = moreDates.length;
    return (
      <>
        <StyledEngineProvider injectFirst>
          {first5Dates.map((day, index) =>
            blackoutEntry(
              day,
              handleRemoveDates,
              blackoutEntryFormat,
              index,
              classes.chip,
            ),
          )}
          {isShowMore &&
            moreDates.map((day, index) =>
              blackoutEntry(
                day,
                handleRemoveDates,
                blackoutEntryFormat,
                index,
                classes.chip,
                maxBlackoutEntries,
              ),
            )}
          {moreDatesLen > 0 && (
            <Button
              className={classes.showMore}
              variant="text"
              onClick={() => {
                setIsShowMore(prev => !prev);
              }}
            >
              {isShowMore ? 'show less' : `+ ${moreDatesLen} more`}
            </Button>
          )}
        </StyledEngineProvider>
      </>
    );
  };

  return (
    <StyledEngineProvider injectFirst>
      <div>
        <div ref={containerRef} className={className}>
          <Input
            ref={inputRef}
            value={
              selectedDaysLen > 0
                ? `${selectedDaysLen} ${
                  selectedDaysLen === 1 ? 'date' : 'dates'
                } selected`
                : ''
            }
            onClick={() => !disabled && setShowPopover(true)}
            disabled={disabled}
            error={error}
            size={size}
            rightElement={<img src={CalendarIcon} alt="calendar-icon" />}
            placeholder={placeholder}
            readOnly={true}
          />
        </div>
        <Popover
          open={showPopover}
          anchorEl={containerRef.current}
          onClose={() => setShowPopover(false)}
        >
          <Calendar
            {...calendarProps}
            disabledBefore={disabledBefore}
            disabledAfter={disabledAfter}
            showTodayButton={showTodayButton}
            todayBtnText={todayBtnText}
            dateLabel={dateLabel}
            showDoneBtn
            doneBtnLabel={doneBtnLabel}
            onDoneClick={() => setShowPopover(false)}
            initialDateOpen={initialDateOpen}
            showYearChangeIcon={showYearChangeIcon}
          />
        </Popover>
        <div className={classes.rangeWrapper}>{showSelectedDays()}</div>
      </div>
    </StyledEngineProvider>
  );
}
const ControlledBlackoutCalendar = props => {
  const {
    name,
    control,
    defaultValue = null,
    isRequired = false,
    customValidator,
    blackoutLimit = { month: Infinity, year: Infinity },
    selectedDateMeta = { month: {}, year: {}, count: 0 },
    setSelectedDateMeta,
    ...additional
  } = props;
  const validations = {
    validate: inputValue => {
      if (customValidator) {
        return customValidator(inputValue);
      } else if (isRequired) {
        if (
          isNullOrUndefined(inputValue.from) ||
          isNullOrUndefined(inputValue.to)
        ) {
          return 'This field is required';
        }
      }
      return true;
    },
  };
  if (typeof customValidator === 'function') {
    validations.validate = customValidator;
  }
  const {
    field: { value, onChange },
  } = useController({
    name,
    rules: validations,
    control,
    defaultValue,
  });
  const blackoutCalendarInputProps = useCalendar(
    value,
    true,
    onChange,
    blackoutLimit,
    selectedDateMeta,
    setSelectedDateMeta,
  );
  return (
    <BlackoutCalendar
      {...additional}
      calendarProps={blackoutCalendarInputProps}
    />
  );
};
export { ControlledBlackoutCalendar };
